import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { theme } from '../../styles/theme';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { HomeIcon, MapPinIcon } from '@heroicons/react/24/outline';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const SLIP_RADIUS_M = 35;

const Section = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  margin-bottom: 1.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  margin: 0.5rem 0 0 0;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const SettingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.text.secondary};
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.input};
  color: ${theme.colors.text.primary};
  font-size: 1rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.input};
  color: ${theme.colors.text.primary};
  font-size: 1rem;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${(p) => (p.$primary ? theme.colors.primary.main : theme.colors.background.secondary)};
  color: ${(p) => (p.$primary ? '#fff' : theme.colors.text.primary)};
  border: 1px solid ${(p) => (p.$primary ? theme.colors.primary.main : theme.colors.border.primary)};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const MapWrap = styled.div`
  height: 280px;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${theme.colors.border.primary};
`;

const StatusLine = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  margin: 0 0 1rem 0;
`;

const Hint = styled.p`
  font-size: 0.8rem;
  color: ${theme.colors.text.secondary};
  margin: 0.75rem 0 0 0;
`;

function formatCoords(lat, lng) {
  if (lat == null || lng == null) return 'Not set';
  return `${parseFloat(lat).toFixed(5)}°, ${parseFloat(lng).toFixed(5)}°`;
}

/** Leaflet ignores MapContainer center updates after mount — fly when target moves. */
function MapRecenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!center?.[0] || !center?.[1]) return;
    map.flyTo(center, zoom, { duration: 0.6 });
  }, [map, center?.[0], center?.[1], zoom]);
  return null;
}

const boatIcon = L.divIcon({
  className: 'home-port-boat-marker',
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 0 0 2px #2563eb88"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const HomePortSettings = ({ autoTripEnabled, onAutoTripChange }) => {
  const [vessels, setVessels] = useState([]);
  const [vesselId, setVesselId] = useState('');
  const [homePortName, setHomePortName] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [loadingVessels, setLoadingVessels] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usingGps, setUsingGps] = useState(false);
  const [boatPosition, setBoatPosition] = useState(null);
  const [loadingPosition, setLoadingPosition] = useState(false);

  const selectedVessel = vessels.find((v) => String(v.id) === String(vesselId));

  const loadVessels = useCallback(async () => {
    try {
      setLoadingVessels(true);
      const res = await api.get('/api/vessels');
      const list = res.data?.vessels || res.data || [];
      setVessels(list);
      if (list.length && !vesselId) {
        setVesselId(String(list[0].id));
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load vessels');
    } finally {
      setLoadingVessels(false);
    }
  }, [vesselId]);

  useEffect(() => {
    loadVessels();
  }, [loadVessels]);

  useEffect(() => {
    if (!selectedVessel) return;
    setHomePortName(
      selectedVessel.home_port_name
      || selectedVessel.marina_location
      || `${selectedVessel.vessel_name} — home slip`
    );
    const lat = selectedVessel.home_port_latitude;
    const lng = selectedVessel.home_port_longitude;
    setLatitude(lat != null ? parseFloat(lat) : null);
    setLongitude(lng != null ? parseFloat(lng) : null);
  }, [selectedVessel]);

  const loadBoatPosition = useCallback(async (id) => {
    if (!id) return;
    try {
      setLoadingPosition(true);
      const res = await api.get(`/api/vessels/${id}/live-position`);
      const pos = res.data?.position || res.data;
      if (pos?.latitude != null && pos?.longitude != null) {
        setBoatPosition({
          lat: parseFloat(pos.latitude),
          lng: parseFloat(pos.longitude),
        });
      } else {
        setBoatPosition(null);
      }
    } catch {
      setBoatPosition(null);
    } finally {
      setLoadingPosition(false);
    }
  }, []);

  useEffect(() => {
    setBoatPosition(null);
    loadBoatPosition(vesselId);
  }, [vesselId, loadBoatPosition]);

  const applyVesselHomePort = (vessel) => {
    if (!vessel) return;
    setVessels((prev) => prev.map((v) => (v.id === vessel.id ? { ...v, ...vessel } : v)));
    if (vessel.home_port_name) setHomePortName(vessel.home_port_name);
    if (vessel.home_port_latitude != null) setLatitude(parseFloat(vessel.home_port_latitude));
    if (vessel.home_port_longitude != null) setLongitude(parseFloat(vessel.home_port_longitude));
  };

  const handleUseCurrentPosition = async () => {
    if (!vesselId) return;
    try {
      setUsingGps(true);
      const res = await api.put(`/api/vessels/${vesselId}/home-port`, {
        home_port_name: homePortName.trim() || undefined,
        use_current_position: true,
      });
      const hp = res.data?.home_port;
      const vessel = res.data?.vessel;
      if (hp) {
        setLatitude(hp.latitude);
        setLongitude(hp.longitude);
        setBoatPosition({ lat: parseFloat(hp.latitude), lng: parseFloat(hp.longitude) });
        if (hp.name) setHomePortName(hp.name);
        const src = hp.position_source === 'live_cache' ? 'live GPS' : 'last known GPS';
        toast.success(`Home slip set from ${src}`);
      }
      if (vessel) applyVesselHomePort(vessel);
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Could not read boat GPS';
      toast.error(msg);
    } finally {
      setUsingGps(false);
    }
  };

  const handleSave = async () => {
    if (!vesselId || latitude == null || longitude == null) {
      toast.error('Set a position first (use current boat position)');
      return;
    }
    try {
      setSaving(true);
      const res = await api.put(`/api/vessels/${vesselId}/home-port`, {
        home_port_name: homePortName.trim(),
        latitude,
        longitude,
      });
      if (res.data?.vessel) applyVesselHomePort(res.data.vessel);
      toast.success('Home port saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save home port');
    } finally {
      setSaving(false);
    }
  };

  const hasPin = Number.isFinite(latitude) && Number.isFinite(longitude);
  const hasBoat = boatPosition && Number.isFinite(boatPosition.lat) && Number.isFinite(boatPosition.lng);

  const mapCenter = useMemo(() => {
    if (hasBoat) return [boatPosition.lat, boatPosition.lng];
    if (hasPin) return [latitude, longitude];
    return [44.35, -78.22];
  }, [hasBoat, hasPin, boatPosition, latitude, longitude]);

  const mapZoom = hasBoat || hasPin ? 17 : 10;
  const showMap = hasBoat || hasPin;

  return (
    <Section>
      <SectionHeader>
        <HomeIcon className="w-6 h-6" style={{ color: theme.colors.primary.main }} />
        <div>
          <SectionTitle>Home Slip &amp; Auto-Trips</SectionTitle>
          <SectionDescription>
            Pin your home slip so trips auto-start when you leave and prompt you to complete when you return.
            Set this while tied up at your slip with SignalK connected.
          </SectionDescription>
        </div>
      </SectionHeader>

      <SettingsGrid>
        <SettingItem>
          <Label>Vessel</Label>
          <Select
            value={vesselId}
            onChange={(e) => setVesselId(e.target.value)}
            disabled={loadingVessels || !vessels.length}
          >
            {vessels.map((v) => (
              <option key={v.id} value={v.id}>{v.vessel_name}</option>
            ))}
          </Select>
        </SettingItem>

        <SettingItem>
          <Label>Slip / marina name</Label>
          <Input
            value={homePortName}
            onChange={(e) => setHomePortName(e.target.value)}
            placeholder="e.g. Lakefield Marina — slip 12"
          />
        </SettingItem>

        <SettingItem>
          <Label>
            <input
              type="checkbox"
              checked={!!autoTripEnabled}
              onChange={() => onAutoTripChange?.(!autoTripEnabled)}
              style={{ marginRight: '0.5rem' }}
            />
            Automatically start trips when leaving the slip
          </Label>
        </SettingItem>
      </SettingsGrid>

      <StatusLine>
        {hasPin ? (
          <>Home slip pin: <strong>{formatCoords(latitude, longitude)}</strong> ({SLIP_RADIUS_M} m circle)</>
        ) : (
          <span style={{ color: theme.colors.warning?.main || '#d97706' }}>
            No home slip configured — auto-trip will use a less accurate movement-only fallback.
          </span>
        )}
      </StatusLine>

      <ButtonRow>
        <Button
          type="button"
          $primary
          onClick={handleUseCurrentPosition}
          disabled={!vesselId || usingGps}
        >
          <MapPinIcon className="w-5 h-5" />
          {usingGps ? 'Reading GPS…' : 'Use current boat position'}
        </Button>
        <Button type="button" onClick={handleSave} disabled={!hasPin || saving}>
          {saving ? 'Saving…' : 'Save home port'}
        </Button>
      </ButtonRow>

      {showMap ? (
        <MapWrap>
          <MapContainer
            key={`${vesselId}-${mapCenter[0]}-${mapCenter[1]}`}
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
          >
            <MapRecenter center={mapCenter} zoom={mapZoom} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {hasBoat && (
              <Marker position={[boatPosition.lat, boatPosition.lng]} icon={boatIcon} />
            )}
            {hasPin && (
              <>
                <Marker position={[latitude, longitude]} />
                <Circle
                  center={[latitude, longitude]}
                  radius={SLIP_RADIUS_M}
                  pathOptions={{ color: theme.colors.primary.main, fillOpacity: 0.15 }}
                />
              </>
            )}
          </MapContainer>
        </MapWrap>
      ) : (
        <StatusLine>
          {loadingPosition
            ? 'Loading boat position…'
            : 'No GPS fix yet — connect SignalK, then tap “Use current boat position”.'}
        </StatusLine>
      )}

      <Hint>
        Blue dot = your boat&apos;s current position. Stand at your slip with SignalK connected,
        tap &quot;Use current boat position&quot; to drop the home pin, then save.
        Leaving the {SLIP_RADIUS_M} m circle at ≥1 knot for ~30 seconds starts a trip; returning prompts you to complete it.
      </Hint>
    </Section>
  );
};

export default HomePortSettings;
